import { configParticle, type ParticleCore } from './types';

/** Darken a color by halving each channel. */
function darken(c: number): number {
  return (((c >> 16) & 0xff) >> 1) << 16 | (((c >> 8) & 0xff) >> 1) << 8 | ((c & 0xff) >> 1);
}

/** Lighten a color by mixing toward white. */
function lighten(c: number, t = 0.3): number {
  const r = (c >> 16) & 0xff;
  const g = (c >> 8) & 0xff;
  const b = c & 0xff;
  return (Math.round(r + (255 - r) * t) << 16) | (Math.round(g + (255 - g) * t) << 8) | Math.round(b + (255 - b) * t);
}

export function spawnVoidDeathExplosion(core: ParticleCore, x: number, y: number, playerColor: number, voidY: number, voidColor = 0xff00ff): void {
  const darkAccent = darken(voidColor);
  const lightAccent = lighten(voidColor);

  // === PHASE 1: Flash (immediate, 0ms) ===

  // 24 flash particles — dark contrast + bright void + deep accent + white accent
  for (let i = 0; i < 24; i++) {
    const r = Math.random();
    const flashColor = r < 0.2 ? 0x050008      // near-black
      : r < 0.5 ? voidColor                     // bright void
        : r < 0.75 ? darkAccent                  // deep accent
          : 0xffffff;                              // white accent
    core.spawnParticle(
      x, y, flashColor,
      0, 0.5,
      0.1, 0.2,
      3.0, 5.0,
      0, 0.4,
      flashColor !== 0x050008,
      false,
      flashColor === 0x050008 ? 0.7 : 0.9,
    );
  }

  // Freeze-immune void consume cross — 4 bright + 4 dark impact lines forming X
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.3;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 40,
      expandSpeed: 50,
      color: voidColor,
      lineWidth: 5,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.25;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 30,
      expandSpeed: 30,
      color: darkAccent,
      lineWidth: 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }

  // Dark silhouette flash — 6 near-black soft particles at center (Sakurai contrast/depth)
  for (let i = 0; i < 6; i++) {
    core.spawnParticle(
      x, y, 0x050008,
      0, 0.3,
      0.12, 0.2,
      3.5, 5.5,
      0, 0.4,
      false, false,
      0.7,
    );
  }

  // === PHASE 2: Consumption Burst (80ms) ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 80,
    callback: () => {
      // 100 particles with downward velocity bias + spawn offset
      for (let i = 0; i < 100; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 10;
        const r2 = Math.random();
        const burstColor = r2 < 0.35 ? voidColor
          : r2 < 0.6 ? darkAccent
            : r2 < 0.8 ? playerColor
              : lightAccent;

        const p = core.getInactive();
        if (!p) continue;

        // Spawn offset 10px to avoid center waste
        const ox = Math.cos(angle) * Math.random() * 10;
        const oy = Math.sin(angle) * Math.random() * 10;
        const hard = Math.random() < 0.5;
        configParticle(p, core, {
          x: x + ox,
          y: y + oy,
          vx: Math.cos(angle) * speed * 0.5,
          vy: Math.abs(Math.sin(angle)) * speed * 0.7 + 2,
          color: burstColor,
          life: 0.3 + Math.random() * 0.5,
          scale: 0.4 + Math.random() * 1.0,
          alpha: 0.85,
          gravity: 0.15,
          friction: 0.94,
          hard,
        });
      }

      // 20 downward-pulling streaks — velocity-aligned, biased strongly downward
      for (let i = 0; i < 20; i++) {
        const streakAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.8; // ~downward ± 23°
        const speed = 8 + Math.random() * 10;
        const streakColor = Math.random() < 0.6 ? voidColor : darkAccent;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 20, y,
          streakAngle, speed,
          streakColor,
          0.15 + Math.random() * 0.15,
          0.2 + Math.random() * 0.2,
          0.2, 0.95,
          true, true,
          0.85,
          4 + Math.random() * 2, // stretch 4-6x
        );
      }

      // Vortex ring — 24 particles spiraling inward+downward
      for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * Math.PI * 2;
        const ringRadius = 40;
        const px = x + Math.cos(angle) * ringRadius;
        const py = y + Math.sin(angle) * ringRadius;
        // Tangential velocity + inward pull + downward bias
        const tangent = angle + Math.PI / 2;
        const inward = Math.atan2(y - py, x - px);
        const speed = 4 + Math.random() * 3;
        const vortexColor = Math.random() < 0.5 ? voidColor : lightAccent;

        const p = core.getInactive();
        if (!p) continue;

        configParticle(p, core, {
          x: px,
          y: py,
          vx: Math.cos(tangent) * speed * 0.6 + Math.cos(inward) * speed * 0.4,
          vy: Math.sin(tangent) * speed * 0.6 + Math.sin(inward) * speed * 0.4 + 2,
          color: vortexColor,
          life: 0.3 + Math.random() * 0.2,
          scale: 0.3 + Math.random() * 0.3,
          alpha: 0.8,
          gravity: 0.1,
          friction: 0.96,
          hard: false,
        });
      }

      // 2 shockwaves: bright void + dark contrast
      core.spawnShockwave(x, y, voidColor, 80, 280, 4, 0.3);
      core.spawnShockwave(x, y, 0x110022, 60, 220, 3, 0.25);
    },
  });

  // === PHASE 2b: Smoke Baton Pass (280ms) — delayed from burst ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 280,
    callback: () => {
      for (let i = 0; i < 25; i++) {
        const smokeColor = Math.random() < 0.5 ? 0x110022 : 0x1a0033;
        core.spawnParticle(
          x, y, smokeColor,
          0.5, 3,
          0.6, 1.5,
          1.5, 3.0,
          0.05,
          0.985,
          false, false,
          0.5,
        );
      }
    },
  });

  // === PHASE 3: Edge Ripple (160ms) — expanded ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 160,
    callback: () => {
      // 2 impact lines spreading left/right along void edge — wider (150px)
      const rippleLife = 0.3;
      core.impactLines.push({
        x, y: voidY,
        angle: Math.PI,
        innerRadius: 0,
        outerRadius: 150,
        expandSpeed: 300,
        color: voidColor,
        lineWidth: 3,
        life: rippleLife,
        maxLife: rippleLife,
        freezeImmune: false,
      });
      core.impactLines.push({
        x, y: voidY,
        angle: 0,
        innerRadius: 0,
        outerRadius: 150,
        expandSpeed: 300,
        color: voidColor,
        lineWidth: 3,
        life: rippleLife,
        maxLife: rippleLife,
        freezeImmune: false,
      });

      // 2 downward impact lines at kill point pulling into void
      const downLife = 0.25;
      core.impactLines.push({
        x, y,
        angle: Math.PI / 2,
        innerRadius: 0,
        outerRadius: 80,
        expandSpeed: 200,
        color: voidColor,
        lineWidth: 3,
        life: downLife,
        maxLife: downLife,
        freezeImmune: false,
      });
      core.impactLines.push({
        x, y,
        angle: Math.PI / 2,
        innerRadius: 10,
        outerRadius: 80,
        expandSpeed: 160,
        color: darkAccent,
        lineWidth: 2,
        life: downLife,
        maxLife: downLife,
        freezeImmune: false,
      });

      // 30 edge sparks scattered along void edge — wider X spread (200px)
      for (let i = 0; i < 30; i++) {
        const sx = x + (Math.random() - 0.5) * 200;
        core.spawnParticle(
          sx, voidY, Math.random() < 0.6 ? voidColor : lightAccent,
          1, 4,
          0.3, 0.6,
          0.3, 0.6,
          -0.05,
          0.96,
          true, Math.random() < 0.5,
          0.8,
        );
      }
    },
  });

  // === PHASE 3.5: Consuming Fragments (220ms) ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 220,
    callback: () => {
      for (let i = 0; i < 16; i++) {
        const fragColor = Math.random() < 0.4 ? voidColor
          : Math.random() < 0.5 ? playerColor
            : 0xffffff;

        const p = core.getInactive();
        if (!p) continue;

        // Spawn above kill point, drift downward into void
        const hard = Math.random() < 0.6;
        configParticle(p, core, {
          x: x + (Math.random() - 0.5) * 40,
          y: y - 15 - Math.random() * 25,
          vx: (Math.random() - 0.5) * 3,
          vy: 3 + Math.random() * 5,
          color: fragColor,
          life: 0.3 + Math.random() * 0.3,
          scale: 0.3 + Math.random() * 0.4,
          alpha: 0.9,
          gravity: 0.12,
          friction: 0.97,
          hard,
        });
      }
    },
  });

  // === PHASE 4: Settling Motes (300-600ms, staggered) ===
  for (let i = 0; i < 35; i++) {
    const delay = 300 + Math.random() * 300;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const moteColor = Math.random() < 0.6 ? voidColor : darkAccent;
        const p = core.getInactive();
        if (!p) return;

        configParticle(p, core, {
          x: x + (Math.random() - 0.5) * 60,
          y: y - 20 - Math.random() * 40,
          vx: (Math.random() - 0.5) * 2,
          vy: 2 + Math.random() * 3,
          color: moteColor,
          life: 0.4 + Math.random() * 0.6,
          scale: 0.2 + Math.random() * 0.4,
          alpha: 0.7,
          gravity: 0.08,
          friction: 0.99,
          hard: false,
        });
      },
    });
  }

  // === PHASE 5: End Flash (550ms) — enhanced Sakurai "sudden disappearance" ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 550,
    callback: () => {
      // 12 flash particles
      for (let i = 0; i < 12; i++) {
        const r = Math.random();
        const endColor = r < 0.25 ? 0x050008
          : r < 0.55 ? voidColor
            : r < 0.8 ? darkAccent
              : 0xffffff;
        core.spawnParticle(
          x, y, endColor,
          0, 0.5,
          0.06, 0.12,
          2.0, 4.0,
          0, 0.5,
          endColor !== 0x050008,
          false,
          endColor === 0x050008 ? 0.6 : 0.8,
        );
      }

      // 2 snap shockwaves — void + dark contrast
      core.spawnShockwave(x, y, voidColor, 60, 450, 2, 0.12);
      core.spawnShockwave(x, y, 0x110022, 45, 350, 2, 0.1);

      // 4 impact lines forming brief void cross — Sakurai "sudden disappearance" punctuation
      for (let i = 0; i < 4; i++) {
        const crossAngle = (i / 4) * Math.PI * 2 + Math.PI / 4;
        core.impactLines.push({
          x, y, angle: crossAngle,
          innerRadius: 0,
          outerRadius: 35,
          expandSpeed: 0,
          color: voidColor,
          lineWidth: 3,
          life: 0.08,
          maxLife: 0.08,
          freezeImmune: false,
        });
      }
    },
  });
}

/**
 * Void Projectile Consume — lighter version of void death (~78 particles over 350ms).
 * 5-phase Sakurai choreography: flash → swirl → edge sparks → motes → end flash.
 */
export function spawnVoidProjectileConsume(core: ParticleCore, x: number, voidY: number, voidColor = 0xff00ff): void {
  const darkAccent = darken(voidColor);
  const lightAccent = lighten(voidColor);

  // === PHASE 1: Flash (0ms) ===

  // 10 flash particles — void/accent/black/white
  for (let i = 0; i < 10; i++) {
    const r = Math.random();
    const flashColor = r < 0.2 ? 0x050008
      : r < 0.5 ? voidColor
        : r < 0.75 ? darkAccent
          : 0xffffff;
    core.spawnParticle(
      x, voidY, flashColor,
      0, 0.5,
      0.1, 0.15,
      2.0, 4.0,
      0, 0.4,
      flashColor !== 0x050008,
      false,
      flashColor === 0x050008 ? 0.7 : 0.9,
    );
  }

  // 2 downward impact lines — V shape into void
  for (let i = 0; i < 2; i++) {
    const angle = Math.PI / 2 + (i === 0 ? -0.3 : 0.3);
    core.impactLines.push({
      x, y: voidY,
      angle,
      innerRadius: 0,
      outerRadius: 30,
      expandSpeed: 80,
      color: voidColor,
      lineWidth: 3,
      life: 0.15,
      maxLife: 0.15,
      freezeImmune: false,
    });
  }

  // 3 dark silhouette particles — Sakurai contrast
  for (let i = 0; i < 3; i++) {
    core.spawnParticle(
      x, voidY, 0x050008,
      0, 0.3,
      0.12, 0.2,
      3.0, 4.0,
      0, 0.4,
      false, false,
      0.7,
    );
  }

  // === PHASE 2: Consumption Swirl (+60ms) ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 60,
    callback: () => {
      // 30 particles pulled into void
      for (let i = 0; i < 30; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 6;
        const r2 = Math.random();
        const burstColor = r2 < 0.4 ? voidColor
          : r2 < 0.65 ? darkAccent
            : r2 < 0.85 ? 0x050008
              : 0xffffff;

        const p = core.getInactive();
        if (!p) continue;

        const ox = Math.cos(angle) * Math.random() * 8;
        const oy = Math.sin(angle) * Math.random() * 8;
        const addBlend = burstColor !== 0x050008;
        const hard = Math.random() < 0.5;
        configParticle(p, core, {
          x: x + ox,
          y: voidY + oy,
          vx: Math.cos(angle) * speed * 0.5,
          vy: Math.abs(Math.sin(angle)) * speed * 0.8 + 3, // pulled into void
          color: burstColor,
          life: 0.15 + Math.random() * 0.2,
          scale: 0.3 + Math.random() * 0.6,
          alpha: 0.85,
          gravity: 0.15,
          friction: 0.94,
          addBlend,
          hard,
        });
      }

      // 8 downward streaks
      for (let i = 0; i < 8; i++) {
        const streakAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.6;
        const speed = 6 + Math.random() * 6;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 15, voidY,
          streakAngle, speed,
          Math.random() < 0.6 ? voidColor : darkAccent,
          0.15 + Math.random() * 0.1,
          0.2 + Math.random() * 0.15,
          0.2, 0.95,
          true, true,
          0.85,
          3 + Math.random(),
        );
      }

      // 1 shockwave
      core.spawnShockwave(x, voidY, voidColor, 50, 200, 2, 0.2);
    },
  });

  // === PHASE 3: Edge Sparks (+120ms) ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 120,
    callback: () => {
      // 2 horizontal impact lines along void edge
      for (const dir of [Math.PI, 0]) {
        core.impactLines.push({
          x, y: voidY,
          angle: dir,
          innerRadius: 0,
          outerRadius: 75,
          expandSpeed: 200,
          color: voidColor,
          lineWidth: 2,
          life: 0.2,
          maxLife: 0.2,
          freezeImmune: false,
        });
      }

      // 12 edge sparks
      for (let i = 0; i < 12; i++) {
        const sx = x + (Math.random() - 0.5) * 120;
        core.spawnParticle(
          sx, voidY, Math.random() < 0.6 ? voidColor : lightAccent,
          1, 3,
          0.2, 0.4,
          0.3, 0.5,
          -0.03,
          0.96,
          true, Math.random() < 0.5,
          0.8,
        );
      }
    },
  });

  // === PHASE 4: Settling Motes (+200-400ms staggered) ===
  for (let i = 0; i < 12; i++) {
    const delay = 200 + Math.random() * 200;
    core.pendingBlasts.push({
      timestamp: Date.now() + delay,
      callback: () => {
        const moteColor = Math.random() < 0.6 ? voidColor : darkAccent;
        const p = core.getInactive();
        if (!p) return;

        configParticle(p, core, {
          x: x + (Math.random() - 0.5) * 40,
          y: voidY - 10 - Math.random() * 20,
          vx: (Math.random() - 0.5) * 1.5,
          vy: 1.5 + Math.random() * 2,
          color: moteColor,
          life: 0.4 + Math.random() * 0.4,
          scale: 0.2 + Math.random() * 0.3,
          alpha: 0.7,
          gravity: 0.06,
          friction: 0.99,
          hard: false,
        });
      },
    });
  }

  // === PHASE 5: End Flash (+350ms) — Sakurai punctuation ===
  core.pendingBlasts.push({
    timestamp: Date.now() + 350,
    callback: () => {
      for (let i = 0; i < 6; i++) {
        const r = Math.random();
        const endColor = r < 0.3 ? voidColor : r < 0.6 ? darkAccent : 0xffffff;
        core.spawnParticle(
          x, voidY, endColor,
          0, 0.5,
          0.06, 0.1,
          1.5, 3.0,
          0, 0.5,
          true, false,
          0.8,
        );
      }

      // 1 snap shockwave
      core.spawnShockwave(x, voidY, voidColor, 40, 350, 2, 0.1);
    },
  });
}

/** Foreshadow warning before void retaliation orb launch. */
export function spawnVoidForeshadow(core: ParticleCore, x: number, voidY: number, voidColor = 0xff00ff): void {
  const darkAccent = darken(voidColor);
  const lightAccent = lighten(voidColor);

  // 4 bubbles rising from void edge
  for (let i = 0; i < 4; i++) {
    const p = core.getInactive();
    if (!p) continue;

    configParticle(p, core, {
      x: x + (Math.random() - 0.5) * 30,
      y: voidY + 5,
      vx: (Math.random() - 0.5) * 1,
      vy: -(3 + Math.random() * 3),
      color: Math.random() < 0.6 ? voidColor : darkAccent,
      life: 0.3 + Math.random() * 0.2,
      scale: 0.4 + Math.random() * 0.3,
      alpha: 0.7,
      gravity: -0.02,
      friction: 0.98,
      hard: false,
    });
  }

  // 6 upward motes
  for (let i = 0; i < 6; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 20, voidY,
      Math.random() < 0.7 ? voidColor : lightAccent,
      1, 3,
      0.2, 0.35,
      0.2, 0.4,
      -0.04,
      0.97,
      true, false,
      0.6,
    );
  }

  // 1 small shockwave
  core.spawnShockwave(x, voidY, voidColor, 30, 150, 2, 0.15);

  // 1 center glow particle
  core.spawnParticle(
    x, voidY, voidColor,
    0, 0,
    0.25, 0.3,
    2.0, 3.0,
    0, 0.5,
    true, false,
    0.6,
  );
}

/** Burst effect when void retaliation orb launches upward. */
export function spawnVoidLaunchBurst(core: ParticleCore, x: number, voidY: number, voidColor = 0xff00ff): void {
  // 6 upward flash particles
  for (let i = 0; i < 6; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.0; // upward cone
    const speed = 4 + Math.random() * 6;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 10, voidY,
      angle, speed,
      Math.random() < 0.6 ? voidColor : 0xffffff,
      0.1 + Math.random() * 0.1,
      0.3 + Math.random() * 0.3,
      0.05, 0.96,
      true, true,
      0.9,
    );
  }

  // 2 upward impact lines
  for (let i = 0; i < 2; i++) {
    const angle = -Math.PI / 2 + (i === 0 ? -0.2 : 0.2);
    core.impactLines.push({
      x, y: voidY,
      angle,
      innerRadius: 0,
      outerRadius: 40,
      expandSpeed: 120,
      color: voidColor,
      lineWidth: 3,
      life: 0.15,
      maxLife: 0.15,
      freezeImmune: false,
    });
  }

  // 8 edge sparks — bright, upward velocity
  for (let i = 0; i < 8; i++) {
    const sx = x + (Math.random() - 0.5) * 40;
    core.spawnParticle(
      sx, voidY, Math.random() < 0.5 ? voidColor : 0xffffff,
      2, 5,
      0.15, 0.25,
      0.3, 0.5,
      -0.06,
      0.96,
      true, true,
      0.85,
    );
  }

  // 4 dark contrast particles
  for (let i = 0; i < 4; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 15, voidY, 0x050008,
      0.5, 2,
      0.1, 0.15,
      2.0, 3.0,
      0.02, 0.95,
      false, false,
      0.6,
    );
  }

  // 1 small shockwave
  core.spawnShockwave(x, voidY, voidColor, 35, 250, 2, 0.15);
}
